'use client';

import { Highlight } from 'prism-react-renderer';
import { HTMLProps } from 'react';

import { cn } from '@/lib/utils';

import { DCBrandedTheme } from './DCBrandedTheme';

export interface CodeBlockItemProps {
  code?: string;
  language: keyof typeof languageMap;
  showLineNumbers?: boolean;
  classNames?: {
    root?: string;
    iconButton?: string;
    successIcon?: string;
  };
  ContainerProps?: HTMLProps<HTMLDivElement>;
}

const languageMap = {
  Python: 'python',
  'Node.js': 'jsx',
  Go: 'go',
  'C++': 'cpp',
  PHP: 'jsx',
  Java: 'Kotlin',
  'C#': 'cpp',
  cURL: 'yaml',
  JSON: 'json',
  HTML: 'markdown',
} as const;
export type CodeBlockLanguage = keyof typeof languageMap;
export const codeBlockLanguages = Object.keys(
  languageMap
) as CodeBlockLanguage[];

export function CodeBlockItem({
  code = '',
  language,
  showLineNumbers = false,
  classNames,
  ContainerProps,
}: CodeBlockItemProps) {
  return (
    <div
      {...ContainerProps}
      style={{
        // ensures scrollbars match the dark theme on safari
        colorScheme: 'dark',
        ...ContainerProps?.style,
      }}
      className={cn(
        'flex w-full flex-row justify-between gap-1 text-left',
        classNames?.root,
        ContainerProps?.className
      )}
    >
      <Highlight
        code={code}
        theme={DCBrandedTheme}
        language={languageMap[language]}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            style={style}
            className={cn(
              className,
              'word-break text-code-sample whitespace-pre-wrap font-[monospace]'
            )}
          >
            {tokens.map((line, i) => (
              <div
                key={i}
                {...getLineProps({ line })}
                className={cn(
                  'flex',
                  `min-w-[${String(tokens.length).length}em]`,
                  getLineProps({ line }).className
                )}
              >
                {showLineNumbers && (
                  <span className='mr-4 inline-block text-right text-gray-500/50'>
                    {i + 1}
                  </span>
                )}
                <div>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  );
}
